import React, { useState, useEffect } from "react";
import "./index.scss";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { useTranslation } from "../../../../hooks/useTranslation";

const GallerySection = ({ images, initialIndex = 0, isMobile = false }) => {
  const [current, setCurrent] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [showRotateHint, setShowRotateHint] = useState(false);
  const { t } = useTranslation();

  // 🔹 Synchroniser si initialIndex change
  useEffect(() => {
    setCurrent(initialIndex);
  }, [initialIndex]);

  // 🔹 Show rotate hint for mobile portrait mode
  useEffect(() => {
    if (isMobile) {
      const isPortrait = window.innerHeight > window.innerWidth;
      setShowRotateHint(isPortrait);
      
      // Hide hint after 3 seconds
      if (isPortrait) {
        const timer = setTimeout(() => {
          setShowRotateHint(false);
        }, 3000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [isMobile]);

  const prevSlide = () => {
    setCurrent((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const nextSlide = () => {
    setCurrent((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const getPositionClass = (index) => {
    if (index === current) return "active";
    if (index === (current - 1 + images.length) % images.length) return "left";
    if (index === (current + 1) % images.length) return "right";
    return "hidden";
  };

  // Touch handlers for mobile swipe gestures
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (!isMobile) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!isMobile || !touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // Mobile gallery layout
  if (isMobile) {
    return (
      <div className="mobile-gallery-container">
        {/* Rotate hint for portrait mode */}
        {showRotateHint && (
          <div className="rotate-hint">
            <div className="rotate-icon">📱</div>
            <p>{t('home.gallery.rotateHint')}</p>
          </div>
        )}

        {/* Main full-screen image */}
        <div 
          className="mobile-main-image"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className="mobile-gallery-item active"
            style={{ backgroundImage: `url(${images[current].img})` }}
          >
            <div className="mobile-gallery-caption">{images[current].title}</div>
          </div>
        </div>

        {/* Thumbnail carousel below */}
        <div className="mobile-thumbnail-carousel">
          <div className="mobile-thumbnails">
            {images.map((img, index) => (
              <div
                key={img.id}
                className={`mobile-thumbnail ${index === current ? 'active' : ''}`}
                onClick={() => setCurrent(index)}
                style={{ backgroundImage: `url(${img.img})` }}
              />
            ))}
          </div>
        </div>

        {/* Navigation arrows for mobile */}
        <button className="mobile-nav-btn left" onClick={prevSlide}>
          <FaArrowLeft />
        </button>
        <button className="mobile-nav-btn right" onClick={nextSlide}>
          <FaArrowRight />
        </button>
      </div>
    );
  }

  // Desktop gallery layout (existing)
  return (
    <div className="gallery-container">
      <button className="nav-btn left" onClick={prevSlide}>
        <FaArrowLeft />
      </button>

      <div className="gallery-wrapper">
        {images.map((img, index) => (
          <div
            key={img.id}
            className={`gallery-item ${getPositionClass(index)}`}
            style={{ backgroundImage: `url(${img.img})` }}>
            {" "}
            <div className="gallery-caption">{img.title}</div>
          </div>
        ))}
      </div>

      <button className="nav-btn right" onClick={nextSlide}>
        <FaArrowRight />
      </button>
    </div>
  );
};

export default GallerySection;
